import React, { useEffect, useRef, useState, useCallback } from 'react'
import { supabase, AUTO_REFRESH_ENABLED } from '../supabaseClient'

import { Panel, Empty, Inp, Btn } from './Layout'

// Admin user IDs for in-app notifications. Uses the admin_ids() database
// function (SUPABASE_CLASS_AUTOMATION.sql): teachers and students
// can't read admin profiles directly.
async function fetchAdminIds() {
  const { data, error } = await supabase.rpc('admin_ids')
  if (error || !data) return []
  return data.map(row => (typeof row === 'string' ? row : row.admin_ids || row.id)).filter(Boolean)
}

function teacherRoom(teacherId, studentId) { return `teacher_msg_${teacherId}_${studentId}` }

function timeFmt(ts) {
  return new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
}

// ── Admin chat — students raise tickets/chat, teachers message admin ─────────
function AdminChat({ profile }) {
  const [messages, setMessages] = useState([])
  const [msg,      setMsg]      = useState('')
  const [sending,  setSending]  = useState(false)
  const [ticket,   setTicket]   = useState('')
  const [category, setCategory] = useState('general')
  const [showForm, setShowForm] = useState(false)
  const [ticketOk, setTicketOk] = useState('')
  const bottomRef = useRef(null)

  const isStudent = profile.role === 'student'
  const room = `support_${profile.id}` // each user has own support room with admin

  useEffect(() => {
    loadMessages()
    if (!AUTO_REFRESH_ENABLED) return

    const channel = supabase
      .channel(`support-room-${profile.id}`)
      .on('postgres_changes', {
        event: 'INSERT', schema: 'public', table: 'chat_messages',
        filter: `room=eq.${room}`,
      }, (payload) => {
        setMessages(prev => [...prev, payload.new])
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
      })
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [profile.id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMessages() {
    const { data } = await supabase
      .from('chat_messages')
      .select('*, profiles(full_name, role)')
      .eq('room', room)
      .order('created_at', { ascending: true })
      .limit(100)
    setMessages(data || [])
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }

  async function sendMsg(e) {
    e.preventDefault()
    if (!msg.trim()) return
    setSending(true)
    await supabase.from('chat_messages').insert({
      sender_id: profile.id,
      room,
      content: msg.trim(),
    })
    setMsg('')
    setSending(false)
  }

  async function submitTicket(e) {
    e.preventDefault()
    if (!ticket.trim()) return
    setSending(true)

    const { error: ticketErr } = await supabase.from('chat_messages').insert({
      sender_id: profile.id,
      room,
      content: `[TICKET — ${category.toUpperCase()}] ${ticket.trim()}`,
    })
    if (ticketErr) {
      // Keep the typed ticket so it isn't lost.
      setTicketOk(`⚠ Ticket could not be submitted: ${ticketErr.message}. Please try again or message us on WhatsApp.`)
      setSending(false)
      return
    }

    const adminIds = await fetchAdminIds()
    if (adminIds.length) {
      const notifs = adminIds.map(id => ({
        user_id: id,
        type:    'support_ticket',
        title:   `🎫 New Support Ticket from ${profile.full_name || profile.email}`,
        message: `[${category}] ${ticket.trim()}`,
        is_read: false,
      }))
      await supabase.from('notifications').insert(notifs)
    }

    setTicket('')
    setShowForm(false)
    setTicketOk('✓ Ticket submitted! Admin will respond here shortly.')
    setTimeout(() => setTicketOk(''), 4000)
    setSending(false)
  }

  const categories = ['general', 'technical', 'payment', 'attendance', 'schedule', 'other']

  return (
    <div>
      {isStudent && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                padding: '7px 14px', borderRadius: '9px', fontSize: '12px', fontWeight: 700,
                background: 'rgba(30,144,255,0.12)', color: '#5aabff',
                border: '0.5px solid rgba(30,144,255,0.25)', cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              🎫 {showForm ? 'Cancel Ticket' : 'Raise a Ticket'}
            </button>
            <span style={{ fontSize: '11px', color: 'rgba(var(--fg-rgb),var(--a25))' }}>or just chat below</span>
          </div>

          {showForm && (
            <form onSubmit={submitTicket} style={{ background: 'rgba(30,144,255,0.06)', border: '0.5px solid rgba(30,144,255,0.2)', borderRadius: '12px', padding: '14px', marginBottom: '10px' }}>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(var(--fg-rgb),var(--a35))', letterSpacing: '0.1em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Category</label>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {categories.map(c => (
                    <button
                      key={c} type="button"
                      onClick={() => setCategory(c)}
                      style={{
                        padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'inherit', border: 'none', textTransform: 'capitalize',
                        background: category === c ? '#1e90ff' : 'rgba(var(--fg-rgb),0.06)',
                        color: category === c ? '#fff' : 'rgba(var(--fg-rgb),var(--a40))',
                      }}
                    >{c}</button>
                  ))}
                </div>
              </div>
              <textarea
                value={ticket}
                onChange={e => setTicket(e.target.value)}
                placeholder="Describe your issue..."
                required
                style={{
                  width: '100%', background: 'rgba(var(--fg-rgb),0.05)',
                  border: '0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius: '10px',
                  padding: '10px 14px', fontSize: '13px', color: 'rgba(var(--fg-rgb),var(--a80))',
                  outline: 'none', resize: 'vertical', minHeight: '80px',
                  fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: '8px',
                }}
              />
              <Btn busy={sending} style={{ margin: 0, width: 'auto', padding: '8px 18px' }}>
                {sending ? 'Submitting...' : 'Submit Ticket'}
              </Btn>
            </form>
          )}

          {ticketOk && (
            <div style={ticketOk.startsWith('⚠')
              ? { padding: '9px 12px', background: 'rgba(239,68,68,0.1)', border: '0.5px solid rgba(239,68,68,0.3)', borderRadius: '8px', fontSize: '13px', color: '#f87171', marginBottom: '8px' }
              : { padding: '9px 12px', background: 'rgba(16,185,129,0.1)', border: '0.5px solid rgba(16,185,129,0.3)', borderRadius: '8px', fontSize: '13px', color: '#34d399', marginBottom: '8px' }}>
              {ticketOk}
            </div>
          )}
        </div>
      )}

      <div style={{
        maxHeight: '360px', overflowY: 'auto', display: 'flex', flexDirection: 'column',
        gap: '8px', marginBottom: '12px', paddingRight: '4px',
      }}>
        {messages.length === 0
          ? <Empty msg={isStudent ? "No messages yet. Raise a ticket or send a message!" : "No messages yet. Start the conversation!"} />
          : messages.map((m, i) => {
              const isMe = m.sender_id === profile.id
              const isTicket = m.content?.startsWith('[TICKET')
              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                  <div style={{ fontSize: '10px', color: 'rgba(var(--fg-rgb),var(--a22))', marginBottom: '3px', textAlign: isMe ? 'right' : 'left' }}>
                    {m.profiles?.full_name || 'Unknown'} · {m.profiles?.role}
                    {' · '}{timeFmt(m.created_at)}
                  </div>
                  <div style={{
                    maxWidth: '78%', padding: '9px 13px',
                    borderRadius: isMe ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                    background: isTicket
                      ? 'rgba(244,163,53,0.12)'
                      : isMe ? 'rgba(30,144,255,0.18)' : 'rgba(var(--fg-rgb),0.06)',
                    border: `0.5px solid ${isTicket ? 'rgba(244,163,53,0.3)' : isMe ? 'rgba(30,144,255,0.3)' : 'rgba(var(--fg-rgb),0.08)'}`,
                    fontSize: '13px', color: 'rgba(var(--fg-rgb),var(--a85))', lineHeight: 1.5,
                  }}>
                    {isTicket && <div style={{ fontSize: '9px', fontWeight: 700, color: '#f4a335', letterSpacing: '0.08em', marginBottom: '4px' }}>🎫 SUPPORT TICKET</div>}
                    {m.content?.replace(/^\[TICKET — \w+\] /, '')}
                  </div>
                </div>
              )
            })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={sendMsg} style={{ display: 'flex', gap: '8px' }}>
        <Inp
          value={msg}
          onChange={e => setMsg(e.target.value)}
          placeholder={isStudent ? "Ask a question or send a message to admin..." : "Type a message to admin..."}
          style={{ margin: 0 }}
        />
        <Btn busy={sending} style={{ width: 'auto', padding: '0 20px', margin: 0 }}>Send</Btn>
      </form>
    </div>
  )
}

// ── Student → their assigned teacher(s) — one-way chat, teacher can't reply here ──
function TeacherChat({ profile }) {
  const [teachers,   setTeachers]   = useState([])
  const [selTeacher, setSelTeacher] = useState('')
  const [messages,   setMessages]   = useState([])
  const [msg,        setMsg]        = useState('')
  const [sending,    setSending]    = useState(false)
  const [loading,    setLoading]    = useState(true)
  const bottomRef = useRef(null)

  useEffect(() => { loadTeachers() }, [profile.id]) // eslint-disable-line react-hooks/exhaustive-deps

  async function loadTeachers() {
    setLoading(true)
    const { data: enr } = await supabase.from('enrollments').select('class_id').eq('student_id', profile.id)
    const classIds = [...new Set((enr || []).map(e => e.class_id).filter(Boolean))]
    if (!classIds.length) { setTeachers([]); setLoading(false); return }
    const { data: cls } = await supabase.from('classes').select('teacher_id,teacher_name').in('id', classIds)
    const map = {}
    ;(cls || []).forEach(c => { if (c.teacher_id) map[c.teacher_id] = c.teacher_name })
    const list = Object.entries(map).map(([id, name]) => ({ id, name: name || 'Teacher' }))
    setTeachers(list)
    setSelTeacher(prev => prev || list[0]?.id || '')
    setLoading(false)
  }

  const room = selTeacher ? teacherRoom(selTeacher, profile.id) : null

  const loadMessages = useCallback(async () => {
    if (!room) { setMessages([]); return }
    const { data } = await supabase.from('chat_messages').select('*, profiles(full_name, role)')
      .eq('room', room).order('created_at', { ascending: true }).limit(100)
    setMessages(data || [])
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
  }, [room])

  useEffect(() => { loadMessages() }, [loadMessages])

  useEffect(() => {
    if (!AUTO_REFRESH_ENABLED || !room) return
    const channel = supabase.channel(`teacher-chat-${room}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `room=eq.${room}` }, (payload) => {
        setMessages(prev => [...prev, payload.new])
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
      })
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [room])

  async function sendMsg(e) {
    e.preventDefault()
    if (!msg.trim() || !room) return
    setSending(true)
    await supabase.from('chat_messages').insert({ sender_id: profile.id, room, content: msg.trim() })
    setMsg('')
    setSending(false)
  }

  const inp = { width: '100%', background: 'var(--bg-input)', border: '0.5px solid rgba(var(--fg-rgb),0.12)', borderRadius: '10px', padding: '8px 12px', fontSize: '13px', color: 'rgba(var(--fg-rgb),var(--a85))', outline: 'none', fontFamily: 'inherit', colorScheme: 'dark' }

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(var(--fg-rgb),var(--a20))', fontSize: '13px' }}>Loading...</div>
  if (!teachers.length) return <Empty msg="You don't have an assigned teacher yet." />

  return (
    <div>
      {teachers.length > 1 && (
        <select value={selTeacher} onChange={e => setSelTeacher(e.target.value)} style={{ ...inp, marginBottom: '12px' }}>
          {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      )}

      <div style={{ maxHeight: '360px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px', paddingRight: '4px' }}>
        {messages.length === 0
          ? <Empty msg="No messages yet. Send your teacher a message!" />
          : messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <div style={{ fontSize: '10px', color: 'rgba(var(--fg-rgb),var(--a22))', marginBottom: '3px' }}>{timeFmt(m.created_at)}</div>
                <div style={{ maxWidth: '78%', padding: '9px 13px', borderRadius: '12px 12px 2px 12px', background: 'rgba(30,144,255,0.18)', border: '0.5px solid rgba(30,144,255,0.3)', fontSize: '13px', color: 'rgba(var(--fg-rgb),var(--a85))', lineHeight: 1.5 }}>
                  {m.content}
                </div>
              </div>
            ))}
        <div ref={bottomRef} />
      </div>

      <div style={{ fontSize: '11px', color: 'rgba(var(--fg-rgb),var(--a25))', marginBottom: '8px' }}>
        Your teacher sees these but replies happen in class / another channel — this isn't a live two-way chat.
      </div>

      <form onSubmit={sendMsg} style={{ display: 'flex', gap: '8px' }}>
        <Inp value={msg} onChange={e => setMsg(e.target.value)} placeholder="Message your teacher..." style={{ margin: 0 }} />
        <Btn busy={sending} style={{ width: 'auto', padding: '0 20px', margin: 0 }}>Send</Btn>
      </form>
    </div>
  )
}

// ── Teacher: read-only inbox of messages sent by students ────────────────────
function StudentMessagesInbox({ profile }) {
  const [messages, setMessages] = useState([])
  const [loading,  setLoading]  = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('chat_messages').select('*, profiles(full_name, role)')
      .like('room', `teacher_msg_${profile.id}_%`)
      .order('created_at', { ascending: false })
      .limit(200)
    setMessages(data || [])
    setLoading(false)
  }, [profile.id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!AUTO_REFRESH_ENABLED) return
    const channel = supabase.channel(`teacher-inbox-${profile.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages' }, (payload) => {
        if (payload.new.room?.startsWith(`teacher_msg_${profile.id}_`)) load()
      })
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [profile.id, load])

  if (loading) return <div style={{ textAlign: 'center', padding: '2rem', color: 'rgba(var(--fg-rgb),var(--a20))', fontSize: '13px' }}>Loading...</div>
  if (!messages.length) return <Empty msg="No messages from students yet." />

  // Group by room (= by student), most recently active student first
  const byRoom = {}
  const roomOrder = []
  messages.forEach(m => {
    if (!byRoom[m.room]) { byRoom[m.room] = []; roomOrder.push(m.room) }
    byRoom[m.room].push(m)
  })

  return (
    <div style={{ display: 'grid', gap: '14px' }}>
      {roomOrder.map(room => {
        const msgs = byRoom[room].slice().reverse() // oldest first within each student
        const studentName = msgs[msgs.length - 1]?.profiles?.full_name || 'Student'
        return (
          <div key={room} style={{ background: 'rgba(var(--fg-rgb),0.03)', border: '0.5px solid rgba(var(--fg-rgb),0.07)', borderRadius: '12px', padding: '12px 14px' }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: '#5aabff', marginBottom: '8px' }}>👤 {studentName}</div>
            <div style={{ display: 'grid', gap: '6px' }}>
              {msgs.map((m, i) => (
                <div key={i} style={{ padding: '8px 12px', background: 'rgba(var(--fg-rgb),0.04)', borderRadius: '9px', fontSize: '13px', color: 'rgba(var(--fg-rgb),var(--a80))' }}>
                  {m.content}
                  <div style={{ fontSize: '10px', color: 'rgba(var(--fg-rgb),var(--a25))', marginTop: '4px' }}>{timeFmt(m.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * SupportChat — shared component for teacher and student dashboards
 * - Everyone: chat/tickets with Admin
 * - Students: also a one-way channel to message their assigned teacher
 * - Teachers: also a read-only inbox of what students sent them (no reply here)
 */
export default function SupportChat({ profile }) {
  const isStudent = profile.role === 'student'
  const isTeacher = profile.role === 'teacher'
  const [view, setView] = useState('admin')

  const tabs = [
    { id: 'admin', label: '💬 Admin' },
    ...(isStudent ? [{ id: 'teacher', label: '👨‍🏫 My Teacher' }] : []),
    ...(isTeacher ? [{ id: 'students', label: '📥 From Students' }] : []),
  ]

  return (
    <Panel title="Support">
      {tabs.length > 1 && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setView(t.id)} style={{
              fontSize: '12px', fontWeight: 700, padding: '7px 14px', borderRadius: '10px', border: 'none',
              cursor: 'pointer', fontFamily: 'inherit',
              background: view === t.id ? '#1e90ff' : 'rgba(var(--fg-rgb),0.06)',
              color:      view === t.id ? '#fff'    : 'rgba(var(--fg-rgb),var(--a45))',
            }}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {view === 'admin'                    && <AdminChat profile={profile} />}
      {view === 'teacher'  && isStudent     && <TeacherChat profile={profile} />}
      {view === 'students' && isTeacher     && <StudentMessagesInbox profile={profile} />}
    </Panel>
  )
}
